import { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Field, Input, Modal, Select } from '@/ui/primitives';
import { ApiError } from '@/lib/api-client';
import { useCliente } from '@/modules/clientes/api';
import {
  produtividadeCreateSchema,
  safraConventionWarning,
  type Produtividade,
  type ProdutividadeCreateInput,
} from '../schemas';
import {
  CULTURA_OPTIONS, FONTE_OPTIONS, TECNOLOGIA_OPTIONS, CULTIVO_OPTIONS,
  MAO_DE_OBRA_OPTIONS, FERTILIDADE_OPTIONS, AGRICULTURA_OPTIONS,
  IRRIGACAO_OPTIONS, STATUS_OPTIONS, TIPO_POSSE_OPTIONS,
  KG_POR_SACA,
} from '../labels';
import { useCreateProdutividade, useUpdateProdutividade } from '../api';

interface ImovelOption {
  key: string;            // 'imovel:<id>' | 'manual'
  imovel_id: number | null;
  matricula: string;
  lote_gleba: string;     // combinado pra autofill do form
  tipo_posse: 'proprio' | 'arrendamento' | null;
  label: string;
}

function combineLoteGleba(lote: string | null | undefined, gleba: string | null | undefined): string {
  return [lote, gleba].map((v) => (v ?? '').trim()).filter(Boolean).join(' · ');
}

type Mode =
  | { kind: 'create'; cpf: string; clienteId: number }
  | { kind: 'edit'; cpf: string; clienteId: number; record: Produtividade };

interface Props {
  mode: Mode | null;
  onClose: () => void;
}

export function ProdutividadeEditModal({ mode, onClose }: Props) {
  const create = useCreateProdutividade(mode?.cpf ?? null);
  const update = useUpdateProdutividade(mode?.cpf ?? null);
  const isEdit = mode?.kind === 'edit';
  const editing = isEdit ? mode.record : null;

  // Lista de imóveis do cliente pra usar no select (próprios + arrendamentos com FK).
  const { data: cliente } = useCliente(mode?.cpf ?? null);

  const imovelOptions = useMemo<ImovelOption[]>(() => {
    if (!cliente) return [];
    const list: ImovelOption[] = [];
    for (const im of cliente.imoveis ?? []) {
      // lote/gleba podem não estar tipados no schema de cliente — mesmo
      // tratamento defensivo do bloco de arrendamentos abaixo.
      const imRaw = im as unknown as Record<string, unknown>;
      list.push({
        key: `imovel:${im.id}`,
        imovel_id: Number(im.id),
        matricula: im.matricula ?? '',
        lote_gleba: combineLoteGleba(
          typeof imRaw.lote === 'string' ? imRaw.lote : null,
          typeof imRaw.gleba === 'string' ? imRaw.gleba : null,
        ),
        tipo_posse: 'proprio',
        label: `${im.denominacao ?? 'Sem denominação'} · Matrícula ${im.matricula ?? '—'} · Próprio`,
      });
    }
    for (const arr of cliente.arrendamentos ?? []) {
      if (arr.imovel?.id) {
        const imovelRaw = arr.imovel as Record<string, unknown>;
        list.push({
          key: `imovel:${arr.imovel.id}`,
          imovel_id: Number(arr.imovel.id),
          matricula: arr.imovel_matricula ?? '',
          lote_gleba: combineLoteGleba(
            typeof imovelRaw.lote === 'string' ? imovelRaw.lote : null,
            typeof imovelRaw.gleba === 'string' ? imovelRaw.gleba : null,
          ),
          tipo_posse: 'arrendamento',
          label: `${arr.imovel_denominacao ?? 'Sem denominação'} · Matrícula ${arr.imovel_matricula ?? '—'} · Arrendamento`,
        });
      }
    }
    return list;
  }, [cliente]);

  const form = useForm<ProdutividadeCreateInput>({
    resolver: zodResolver(produtividadeCreateSchema),
    defaultValues: defaultsFor(mode, editing),
  });

  // Reset quando o modal abre / muda de registro
  useEffect(() => {
    if (mode) form.reset(defaultsFor(mode, editing));
  }, [mode, editing, form]);

  // Sync sc_ha ↔ kg_ha (×60). Atualiza pelo último editado, sem loop.
  const scHa = form.watch('sc_ha');
  const kgHa = form.watch('kg_ha');
  useEffect(() => {
    if (scHa == null) return;
    const target = Number(scHa) * KG_POR_SACA;
    if (kgHa == null || Math.abs(Number(kgHa) - target) > 0.001) {
      form.setValue('kg_ha', Number(target.toFixed(2)), { shouldDirty: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scHa]);

  // Quando seleciona imóvel cadastrado, autopreenche matrícula, lote/gleba e tipo_posse
  const imovelId = form.watch('imovel_id');
  useEffect(() => {
    if (imovelId == null) return;
    const sel = imovelOptions.find((o) => o.imovel_id === Number(imovelId));
    if (sel) {
      form.setValue('matricula', sel.matricula, { shouldDirty: true });
      form.setValue('tipo_posse', sel.tipo_posse, { shouldDirty: true });
      // lote guarda o valor combinado "lote · gleba"; gleba fica null nos registros novos
      form.setValue('lote', sel.lote_gleba || null, { shouldDirty: true });
      form.setValue('gleba', null, { shouldDirty: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imovelId]);

  function onSubmit(values: ProdutividadeCreateInput) {
    // Limpa strings vazias pra null (a maioria dos enums e textos opcionais)
    const cleaned = Object.fromEntries(
      Object.entries(values).map(([k, v]) => [k, v === '' ? null : v]),
    ) as ProdutividadeCreateInput;

    if (mode?.kind === 'create') {
      create.mutate(cleaned, {
        onSuccess: () => onClose(),
        onError: (err) => alertError(err),
      });
    } else if (mode?.kind === 'edit') {
      update.mutate(
        { id: mode.record.id, ...cleaned },
        {
          onSuccess: () => onClose(),
          onError: (err) => alertError(err),
        },
      );
    }
  }

  const submitting = create.isPending || update.isPending;

  return (
    <Modal
      open={mode != null}
      onOpenChange={(open) => { if (!open) onClose(); }}
      title={isEdit ? 'Editar registro de produtividade' : 'Novo registro de produtividade'}
      size="3xl"
    >
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5">
        {/* ── Imóvel/talhão ── */}
        <section className="flex flex-col gap-3">
          <h3 className="text-[0.72rem] uppercase tracking-wider text-text-2 font-semibold">Imóvel/Talhão</h3>
          <Field label="Imóvel" htmlFor="prod-imovel">
            <Controller
              control={form.control}
              name="imovel_id"
              render={({ field }) => (
                <Select
                  id="prod-imovel"
                  value={field.value == null ? '' : String(field.value)}
                  onChange={(e) => {
                    const v = e.target.value;
                    field.onChange(v === '' ? null : Number(v));
                  }}
                >
                  <option value="">— Não cadastrado / informar manualmente —</option>
                  {imovelOptions.map((o) => (
                    <option key={o.key} value={String(o.imovel_id)}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              )}
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Matrícula" htmlFor="prod-matricula">
              <Input id="prod-matricula" {...form.register('matricula')} placeholder="ex.: 9.830" />
            </Field>
            <Field
              label="Lote / Gleba"
              htmlFor="prod-lote-gleba"
              hint="Vem do imóvel selecionado. Edite se precisar discriminar talhão."
            >
              <Input id="prod-lote-gleba" {...form.register('lote')} placeholder="ex.: 69-A · Gleba 3" />
            </Field>
          </div>

          <Field label="Posse" htmlFor="prod-posse">
            <Controller
              control={form.control}
              name="tipo_posse"
              render={({ field }) => (
                <Select
                  id="prod-posse"
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                >
                  <option value="">—</option>
                  {TIPO_POSSE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Select>
              )}
            />
          </Field>
        </section>

        {/* ── Cultura ── */}
        <section className="flex flex-col gap-3">
          <h3 className="text-[0.72rem] uppercase tracking-wider text-text-2 font-semibold">Cultura</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Cultura" htmlFor="prod-cultura" required>
              <Controller
                control={form.control}
                name="cultura"
                render={({ field }) => (
                  <Select
                    id="prod-cultura"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  >
                    <option value="">—</option>
                    {CULTURA_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                )}
              />
            </Field>
            <Field
              label="Safra"
              htmlFor="prod-safra"
              required
              error={form.formState.errors.safra?.message}
              hint={
                safraConventionWarning(form.watch('cultura'), form.watch('safra'))
                ?? (form.watch('cultura') === 'milho'
                  ? 'Milho safrinha = 2ª safra. Plantio e colheita no mesmo ano civil (ex.: 2026/2026).'
                  : 'Soja é safra de verão. Anos consecutivos (ex.: 2025/2026).')
              }
            >
              <Input
                id="prod-safra"
                {...form.register('safra')}
                placeholder={form.watch('cultura') === 'milho' ? '2026/2026' : '2025/2026'}
                invalid={!!form.formState.errors.safra}
              />
            </Field>
          </div>
        </section>

        {/* ── Produtividade ── */}
        <section className="flex flex-col gap-3">
          <h3 className="text-[0.72rem] uppercase tracking-wider text-text-2 font-semibold">Produtividade</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Sacas / ha" htmlFor="prod-scha" hint="Atualiza Kg/ha (×60)">
              <Input
                id="prod-scha"
                type="number"
                step="0.01"
                {...form.register('sc_ha', { setValueAs: (v) => v === '' ? null : Number(v) })}
                placeholder="ex.: 60"
              />
            </Field>
            <Field label="Kg / ha" htmlFor="prod-kgha" hint="Padrão 60 kg/saca">
              <Input
                id="prod-kgha"
                type="number"
                step="0.01"
                {...form.register('kg_ha', { setValueAs: (v) => v === '' ? null : Number(v) })}
                placeholder="ex.: 3600"
              />
            </Field>
          </div>
        </section>

        {/* ── Origem ── */}
        <section className="flex flex-col gap-3">
          <h3 className="text-[0.72rem] uppercase tracking-wider text-text-2 font-semibold">Origem</h3>
          <Field label="Fonte" htmlFor="prod-fonte">
            <Controller
              control={form.control}
              name="fonte"
              render={({ field }) => (
                <Select
                  id="prod-fonte"
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                >
                  <option value="">—</option>
                  {FONTE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Select>
              )}
            />
          </Field>
        </section>

        {/* ── Manejo ── */}
        <section className="flex flex-col gap-3">
          <h3 className="text-[0.72rem] uppercase tracking-wider text-text-2 font-semibold">Manejo</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Tecnologia" htmlFor="prod-tec">
              <Controller
                control={form.control}
                name="tecnologia"
                render={({ field }) => (
                  <Select id="prod-tec" value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}>
                    <option value="">—</option>
                    {TECNOLOGIA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Select>
                )}
              />
            </Field>
            <Field label="Cultivo" htmlFor="prod-cultivo">
              <Controller
                control={form.control}
                name="cultivo"
                render={({ field }) => (
                  <Select id="prod-cultivo" value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}>
                    <option value="">—</option>
                    {CULTIVO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Select>
                )}
              />
            </Field>
            <Field label="Mão de obra" htmlFor="prod-mao">
              <Controller
                control={form.control}
                name="mao_de_obra"
                render={({ field }) => (
                  <Select id="prod-mao" value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}>
                    <option value="">—</option>
                    {MAO_DE_OBRA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Select>
                )}
              />
            </Field>
            <Field label="Fertilidade" htmlFor="prod-fert">
              <Controller
                control={form.control}
                name="fertilidade"
                render={({ field }) => (
                  <Select id="prod-fert" value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}>
                    <option value="">—</option>
                    {FERTILIDADE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Select>
                )}
              />
            </Field>
            <Field label="Agricultura" htmlFor="prod-agri">
              <Controller
                control={form.control}
                name="agricultura"
                render={({ field }) => (
                  <Select id="prod-agri" value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}>
                    <option value="">—</option>
                    {AGRICULTURA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Select>
                )}
              />
            </Field>
            <Field label="Irrigação" htmlFor="prod-irri">
              <Controller
                control={form.control}
                name="irrigacao"
                render={({ field }) => (
                  <Select id="prod-irri" value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}>
                    <option value="">—</option>
                    {IRRIGACAO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Select>
                )}
              />
            </Field>
          </div>
        </section>

        {/* ── Status ── */}
        <section className="flex flex-col gap-3">
          <h3 className="text-[0.72rem] uppercase tracking-wider text-text-2 font-semibold">Estado</h3>
          <Field label="Status" htmlFor="prod-status">
            <Controller
              control={form.control}
              name="status"
              render={({ field }) => (
                <Select id="prod-status" value={field.value ?? 'pendente'} onChange={(e) => field.onChange(e.target.value)}>
                  {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
              )}
            />
          </Field>
        </section>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>Cancelar</Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Cadastrar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function defaultsFor(mode: Mode | null, editing: Produtividade | null): ProdutividadeCreateInput {
  // Ao editar, combina lote + gleba do registro antigo em um único campo `lote`
  // pra exibir no input combinado. `gleba` vai null nos saves novos.
  const loteCombinado = editing ? combineLoteGleba(editing.lote, editing.gleba) || null : null;
  return {
    cliente_id: mode?.clienteId ?? 0,
    imovel_id: editing?.imovel_id ?? null,
    matricula: editing?.matricula ?? null,
    lote: loteCombinado,
    gleba: null,
    tipo_posse: editing?.tipo_posse ?? null,
    cultura: editing?.cultura ?? ('soja' as const),
    safra: editing?.safra ?? '',
    sc_ha: editing?.sc_ha ?? null,
    kg_ha: editing?.kg_ha ?? null,
    fonte: editing?.fonte ?? null,
    tecnologia: editing?.tecnologia ?? null,
    cultivo: editing?.cultivo ?? null,
    mao_de_obra: editing?.mao_de_obra ?? null,
    fertilidade: editing?.fertilidade ?? null,
    agricultura: editing?.agricultura ?? null,
    irrigacao: editing?.irrigacao ?? null,
    status: editing?.status ?? 'pendente',
    observacoes: editing?.observacoes ?? null,
  };
}

function alertError(err: unknown) {
  const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Erro desconhecido';
  alert(`Falha ao salvar: ${msg}`);
}
